import axios, { AxiosInstance } from 'axios';
import { LoginRequest, LoginResponse, User, Tenant, Restaurant, Table, TableLayoutUpdate, MenuCategory, MenuItem, Order, OrderItem, ApiResponse, UserRole, UserStatus, PricingPlan, ReportTemplate, ReportExecution, DashboardSnapshot, SubscriptionPayment, KOTConfig } from '../types';
import { globalIsOnline } from '../contexts/NetworkContext';
import { localDb } from './localDb';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5134/api';
const isDev = process.env.NODE_ENV !== 'production';

if (isDev) console.log('API Base URL configured:', API_BASE_URL);

// Check if demo mode is enabled
const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';
if (isDev) console.log('Demo mode:', isDemoMode);

type OfflineOrderQueueEntry = Order & {
  syncStatus?: 'pending' | 'syncing' | 'failed';
  syncError?: string | null;
  syncedOrderId?: string | null;
  syncedAt?: string | null;
  localCreatedAt?: string;
  paidAmount?: number;
  paymentTenderedAmount?: number | null;
  paymentTransactionReference?: string | null;
  items?: Array<{
    menuItemId: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    specialInstructions?: string;
  }>;
  splitPayments?: Array<{
    method: number;
    amount: number;
    gatewayName?: string;
  }>;
  paymentPayload?: {
    method: number;
    amount: number;
    tenderedAmount?: number;
    transactionId?: string;
  } | null;
};

class ApiService {
  private client: AxiosInstance;
  private token: string | null = null;
  private syncInFlight: Promise<{ synced: number; failed: number }> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    // Load token from localStorage on initialization
    this.token = localStorage.getItem('auth_token');
    if (this.token) {
      this.setAuthToken(this.token);
    }

    // Add request interceptor for logging (dev only)
    this.client.interceptors.request.use(
      (config) => {
        if (isDev) {
          console.log('API Request:', {
            url: config.url,
            method: config.method,
            data: config.data ? { ...config.data, password: '***' } : config.data,
          });
        }
        return config;
      },
      (error) => {
        if (isDev) console.error('Request error:', error);
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => {
        if (isDev) {
          console.log('API Response:', { status: response.status, url: response.config.url });
        }
        return response;
      },
      (error) => {
        if (error.response) {
          if (isDev) console.error('API Error:', error.response.status, error.response.config?.url);
          if (isDev) console.error('Error response data:', error.response.data);
        } else if (error.request) {
          if (isDev) console.error('No response received:', error.request);
        } else {
          if (isDev) console.error('Error setting up request:', error.message);
        }
        if (error.response?.status === 401) {
          localStorage.removeItem('auth_token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  setAuthToken(token: string) {
    this.token = token;
    this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('auth_token', token);
  }

  clearAuthToken() {
    this.token = null;
    delete this.client.defaults.headers.common['Authorization'];
    localStorage.removeItem('auth_token');
  }

  setToken(token: string) { this.setAuthToken(token); }
  clearToken() { this.clearAuthToken(); }

  private isOfflineFriendlyError(error: any): boolean {
    return !error?.response;
  }

  private async getCachedValue<T>(key: string, fallback: T): Promise<T> {
    const cached = await localDb.getCache(key);
    return (cached ?? fallback) as T;
  }

  private async setCachedValue(key: string, value: any): Promise<void> {
    try {
      await localDb.setCache(key, value);
    } catch (err) {
      console.warn(`Failed to cache ${key}:`, err);
    }
  }

  private buildOfflineOrderEntry(orderData: Partial<Order>, existingOrder?: OfflineOrderQueueEntry | null): OfflineOrderQueueEntry {
    const prefix = orderData.orderType === 2 ? 'OFF-TK' : orderData.orderType === 3 ? 'OFF-DL' : 'OFF-DN';
    const orderNumber = existingOrder?.orderNumber || ('#' + prefix + '-' + Math.floor(1000 + Math.random() * 9000));
    const items = ((orderData as any).items || orderData.orderItems || []).map((item: any) => ({
      menuItemId: item.menuItemId,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      totalPrice: item.totalPrice,
      specialInstructions: item.specialInstructions || undefined,
    }));

    const mergedItems = [...(existingOrder?.items || [])];
    for (const item of items) {
      const existingItem = mergedItems.find((queuedItem) =>
        queuedItem.menuItemId === item.menuItemId &&
        (queuedItem.specialInstructions || '') === (item.specialInstructions || '')
      );

      if (existingItem) {
        existingItem.quantity += item.quantity;
        existingItem.totalPrice += item.totalPrice;
      } else {
        mergedItems.push({ ...item });
      }
    }

    return {
      id: existingOrder?.id || orderData.id || `offline-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      restaurantId: orderData.restaurantId || existingOrder?.restaurantId || '',
      tableId: orderData.tableId || existingOrder?.tableId || undefined,
      userId: orderData.userId || existingOrder?.userId || undefined,
      orderNumber,
      orderType: orderData.orderType ?? existingOrder?.orderType ?? 1,
      status: existingOrder?.status || ('Pending' as any),
      source: 1,
      subtotal: existingOrder ? (existingOrder.subtotal ?? 0) + (orderData.subtotal ?? 0) : (orderData.subtotal ?? 0),
      taxAmount: existingOrder ? (existingOrder.taxAmount ?? 0) + (orderData.taxAmount ?? 0) : (orderData.taxAmount ?? 0),
      discountAmount: existingOrder ? (existingOrder.discountAmount ?? 0) + (orderData.discountAmount ?? 0) : (orderData.discountAmount ?? 0),
      totalAmount: existingOrder ? (existingOrder.totalAmount ?? 0) + (orderData.totalAmount ?? 0) : (orderData.totalAmount ?? 0),
      finalAmount: existingOrder
        ? (existingOrder.finalAmount ?? existingOrder.totalAmount ?? 0) + ((orderData as any).finalAmount ?? orderData.totalAmount ?? 0)
        : ((orderData as any).finalAmount ?? orderData.totalAmount ?? 0),
      notes: [existingOrder?.notes, orderData.notes].filter(Boolean).join('; '),
      orderDate: existingOrder?.orderDate || new Date().toISOString(),
      createdAt: existingOrder?.createdAt || new Date(),
      updatedAt: new Date(),
      orderItems: mergedItems.map((item: any) => ({
        ...item,
        id: item.id || `offline-item-${Math.random().toString(36).slice(2, 6)}`,
        orderId: '',
        createdAt: new Date(),
      })),
      items: mergedItems,
      isOffline: true as any,
      paymentStatus: existingOrder?.paymentStatus || 'Pending',
      syncStatus: 'pending',
      syncedOrderId: existingOrder?.syncedOrderId ?? null,
      syncedAt: existingOrder?.syncedAt ?? null,
      syncError: null,
      localCreatedAt: existingOrder?.localCreatedAt || new Date().toISOString(),
      paymentPayload: existingOrder?.paymentPayload ?? null,
      paidAmount: existingOrder?.paidAmount,
      paymentTenderedAmount: existingOrder?.paymentTenderedAmount ?? null,
      paymentTransactionReference: existingOrder?.paymentTransactionReference ?? null,
      splitPayments: existingOrder?.splitPayments ? [...existingOrder.splitPayments] : undefined,
    };
  }

  private async saveOfflineOrder(orderData: Partial<Order>): Promise<Order> {
    const offlineOrderId = typeof orderData.id === 'string' && orderData.id.startsWith('offline-')
      ? orderData.id
      : null;
    const existingOrder = offlineOrderId
      ? await localDb.getPendingOrderById(offlineOrderId) as OfflineOrderQueueEntry | null
      : null;
    const localOrder = this.buildOfflineOrderEntry(orderData, existingOrder);
    await localDb.addPendingOrder(localOrder);
    return localOrder;
  }

  // Helper method to decode JWT token
  private decodeJwtToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Failed to decode JWT token:', error);
      return {};
    }
  }

  // Transform backend menu item to frontend format
  private transformMenuItemFromBackend(backendItem: any): MenuItem {
    const rawTags = backendItem.tags || backendItem.Tags || '';
    const tagsArray = typeof rawTags === 'string'
      ? (rawTags.trim() ? rawTags.split(',').map((t: string) => t.trim()) : [])
      : (Array.isArray(rawTags) ? rawTags : []);

    // Sizes are stored as a JSON string on the backend; parse defensively.
    const rawSizes = backendItem.sizes ?? backendItem.Sizes;
    let sizesArray: { name: string; price: number }[] = [];
    if (Array.isArray(rawSizes)) {
      sizesArray = rawSizes;
    } else if (typeof rawSizes === 'string' && rawSizes.trim()) {
      try {
        const parsed = JSON.parse(rawSizes);
        if (Array.isArray(parsed)) {
          sizesArray = parsed
            .filter((s: any) => s && (s.name ?? s.Name))
            .map((s: any) => ({ name: String(s.name ?? s.Name), price: Number(s.price ?? s.Price) || 0 }));
        }
      } catch {
        sizesArray = [];
      }
    }

    return {
      id: backendItem.id || backendItem.Id,
      categoryId: backendItem.menuCategoryId || backendItem.MenuCategoryId,
      name: backendItem.name || backendItem.Name,
      shortcutName: backendItem.shortcutName || backendItem.ShortcutName || undefined,
      description: backendItem.description || backendItem.Description || '',
      price: backendItem.price || backendItem.Price || 0,
      imageUrl: backendItem.imageUrl || backendItem.ImageUrl,
      isAvailable: backendItem.isAvailable !== undefined ? backendItem.isAvailable :
                  (backendItem.IsAvailable !== undefined ? backendItem.IsAvailable : true),
      preparationTime: backendItem.preparationTime || backendItem.PreparationTime || 15,
      isVegetarian: tagsArray.includes('Vegetarian'),
      isSpicy: tagsArray.includes('Spicy'),
      tags: tagsArray,
      sizes: sizesArray,
      isHappyHourEnabled: backendItem.isHappyHourEnabled || backendItem.IsHappyHourEnabled || false,
      happyHourPrice: backendItem.happyHourPrice || backendItem.HappyHourPrice || null,
      happyHourStart: backendItem.happyHourStart || backendItem.HappyHourStart || null,
      happyHourEnd: backendItem.happyHourEnd || backendItem.HappyHourEnd || null,
      happyHourDays: backendItem.happyHourDays || backendItem.HappyHourDays || null,
      createdAt: backendItem.createdAt ? new Date(backendItem.createdAt) : new Date(),
      updatedAt: backendItem.updatedAt ? new Date(backendItem.updatedAt) : new Date(),
    } as any;
  }

  // Transform backend user to frontend format
  private transformUserFromBackend(backendUser: any): User {
    // Parse FullName into firstName and lastName
    const fullName = backendUser.fullName || backendUser.FullName || '';
    const nameParts = fullName.split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    // Map IsActive to status
    const isActive = backendUser.isActive !== undefined ? backendUser.isActive :
                    (backendUser.IsActive !== undefined ? backendUser.IsActive : true);
    const status = isActive ? UserStatus.Active : UserStatus.Inactive;
    
    return {
      id: backendUser.id,
      tenantId: backendUser.tenantId || backendUser.TenantId,
      restaurantId: backendUser.restaurantId || backendUser.RestaurantId,
      username: backendUser.username || backendUser.Username,
      email: backendUser.email || backendUser.Email,
      passwordHash: backendUser.passwordHash || backendUser.PasswordHash,
      firstName,
      lastName,
      role: backendUser.role || backendUser.Role || UserRole.Staff,
      status,
      phone: backendUser.phone || backendUser.Phone || '',
      lastLoginAt: backendUser.lastLoginAt ? new Date(backendUser.lastLoginAt) : undefined,
      createdAt: backendUser.createdAt ? new Date(backendUser.createdAt) : new Date(),
      updatedAt: backendUser.updatedAt ? new Date(backendUser.updatedAt) : new Date(),
    };
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    // Demo mode disabled - always use real API
    try {
      if (isDev) console.log('Attempting login with credentials:', { restaurantCode: credentials.restaurantCode, username: credentials.username, hasPassword: !!credentials.password });

      // Validate input
      if (!credentials.restaurantCode || !credentials.username || !credentials.password) {
        throw new Error('Missing required credentials');
      }
      
      const response = await this.client.post<{token: string, restaurantCode: string, rememberRestaurantCode: boolean}>('/Auth/login', credentials, {
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json',
        }
      });
      if (isDev) console.log('Login API response received:', response.status);
      
      if (!response.data.token) {
        throw new Error('No token received from login');
      }
      
      // Set the auth token
      this.setAuthToken(response.data.token);
      
      // Decode the JWT token to get user information
      const tokenPayload = this.decodeJwtToken(response.data.token);
      if (isDev) console.log('Decoded token payload:', tokenPayload);

      // Get role from token payload (fallback to username-based detection)
      let role: UserRole;
      const roleClaim = tokenPayload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      if (isDev) console.log('Role claim from token:', roleClaim);
      if (roleClaim === 'SuperAdmin' || roleClaim === 'Super Admin') {
        role = UserRole.SuperAdmin;
      } else if (roleClaim === 'Admin') {
        role = UserRole.Admin;
      } else if (roleClaim === 'Staff') {
        role = UserRole.Staff;
      } else {
        // Fallback to username-based detection
        if (isDev) console.log('Falling back to username-based role detection');
        if (credentials.username === 'superadmin') {
          role = UserRole.SuperAdmin;
        } else if (credentials.username === 'admin') {
          role = UserRole.Admin;
        } else {
          role = UserRole.Staff;
        }
      }
      
      // Get first name from token or username
      const firstName = tokenPayload.unique_name ||
        (credentials.username === 'superadmin' ? 'Super' :
         credentials.username === 'admin' ? 'Admin' : 'Staff');
      
      // Create a user object from token payload and credentials
      const user: User = {
        id: tokenPayload.sub || '',
        username: credentials.username,
        email: tokenPayload.email || `${credentials.username}@demo.restaurant.com`,
        passwordHash: '',
        firstName: firstName,
        lastName: 'User',
        role: role,
        status: UserStatus.Active,
        tenantId: tokenPayload.tenant_id || '',
        restaurantId: tokenPayload.restaurant_id || '',
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      if (isDev) console.log('Created user object:', user);

      const loginResponse: LoginResponse = {
        token: response.data.token,
        user: user,
        expiresAt: tokenPayload.exp ? new Date(tokenPayload.exp * 1000) : new Date(Date.now() + 24 * 60 * 60 * 1000)
      };
      
      if (isDev) console.log('Login successful, returning response');
      return loginResponse;
    } catch (error: any) {
      if (isDev) {
        console.error('Login failed:', error);
        if (error.response) {
          console.error('Response data:', error.response.data);
          console.error('Response status:', error.response.status);
        }
      }
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      await this.client.post('/Auth/logout');
    } catch {
      // Ignore logout errors in demo mode
    }
    this.clearAuthToken();
  }

  async getCurrentUser(): Promise<User> {
    const token = this.token;
    
    // Demo mode - return demo user
    if (token && token.startsWith('demo-token-')) {
      const username = token.split('-')[2] || 'demo';
      let role: UserRole = UserRole.Staff;
      if (username === 'superadmin') role = UserRole.SuperAdmin;
      else if (username === 'admin') role = UserRole.Admin;
      
      return {
        id: 'demo-user-id',
        username: username,
        email: `${username}@demo.restaurant.com`,
        passwordHash: '',
        firstName: username === 'superadmin' ? 'Super' : username === 'admin' ? 'Admin' : 'Staff',
        lastName: 'User',
        role: role,
        status: UserStatus.Active,
        tenantId: '39eb1948-3eb5-4cdc-9b37-caea1a394f7d',
        restaurantId: 'a72df374-5613-47d7-af67-dd674c1859c9',
        createdAt: new Date(),
        updatedAt: new Date()
      };
    }
    
    const response = await this.client.get<ApiResponse<User>>('/auth/me');
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to get user');
  }

  // Tenant management (SuperAdmin only)
  async getTenants(): Promise<Tenant[]> {
    const response = await this.client.get<Tenant[]>('/tenant');
    return response.data || [];
  }

  async getTenantById(id: string): Promise<Tenant> {
    const response = await this.client.get<Tenant>(`/tenant/${id}`);
    return response.data;
  }

  async getCurrentTenant(): Promise<Tenant> {
    const response = await this.client.get<Tenant>('/tenant/current');
    return response.data;
  }

  async createTenant(tenant: { name: string; restaurantCode?: string; subdomain: string; planId?: string; planExpiry?: string }): Promise<Tenant> {
    const response = await this.client.post<Tenant>('/tenant', tenant);
    return response.data;
  }

  async updateTenant(id: string, tenant: { name: string; subdomain: string; isActive: boolean; restaurantCode?: string; planId?: string; planExpiry?: string; canAdjustSales?: boolean }): Promise<Tenant> {
    const response = await this.client.put<Tenant>(`/tenant/${id}`, tenant);
    return response.data;
  }

  async deleteTenant(id: string): Promise<{ message: string }> {
    const response = await this.client.delete<{ message: string }>(`/tenant/${id}`);
    return response.data;
  }

  async getTenantRestaurants(tenantId: string): Promise<Restaurant[]> {
    const response = await this.client.get<Restaurant[]>(`/tenant/${tenantId}/restaurants`);
    return response.data || [];
  }

  async getTenantUsers(tenantId: string): Promise<User[]> {
    const response = await this.client.get<User[]>(`/tenant/${tenantId}/users`);
    return response.data || [];
  }

  // Restaurant management
  async getRestaurants(tenantId?: string): Promise<Restaurant[]> {
    if (tenantId) {
      const response = await this.client.get<Restaurant[]>(`/restaurant/tenant/${tenantId}`);
      return response.data || [];
    }
    // Fallback to generic endpoint if no tenantId
    const response = await this.client.get<Restaurant[]>('/restaurant');
    return response.data || [];
  }

  async getRestaurantById(id: string): Promise<Restaurant> {
    const response = await this.client.get<Restaurant>(`/restaurant/${id}`);
    return response.data;
  }

  async createRestaurant(restaurant: Partial<Restaurant>): Promise<Restaurant> {
    const response = await this.client.post<Restaurant>('/restaurant', restaurant);
    return response.data;
  }

  async updateRestaurant(id: string, restaurant: Partial<Restaurant>): Promise<void> {
    await this.client.put(`/restaurant/${id}`, restaurant);
  }

  async deleteRestaurant(id: string): Promise<void> {
    await this.client.delete(`/restaurant/${id}`);
  }

  // Table management
  async getTables(restaurantId: string): Promise<Table[]> {
    const cacheKey = `tables:${restaurantId}`;
    try {
      const response = await this.client.get<Table[]>(`/table/restaurant/${restaurantId}`);
      await this.setCachedValue(cacheKey, response.data || []);
      return response.data || [];
    } catch (error) {
      if (!globalIsOnline || this.isOfflineFriendlyError(error)) {
        return await this.getCachedValue<Table[]>(cacheKey, []);
      }
      throw error;
    }
  }

  async getActiveTables(restaurantId: string): Promise<Table[]> {
    const cacheKey = `tables:${restaurantId}`;
    try {
      const response = await this.client.get<Table[]>(`/table/restaurant/${restaurantId}/active`);
      await this.setCachedValue(cacheKey, response.data || []);
      return response.data || [];
    } catch (error) {
      if (!globalIsOnline || this.isOfflineFriendlyError(error)) {
        return await this.getCachedValue<Table[]>(cacheKey, []);
      }
      throw error;
    }
  }

  async getTableById(id: string): Promise<Table> {
    const response = await this.client.get<Table>(`/table/${id}`);
    return response.data;
  }

  async createTable(table: Partial<Table>): Promise<Table> {
    const response = await this.client.post<Table>('/table', table);
    return response.data;
  }

  async updateTable(id: string, table: Partial<Table>): Promise<void> {
    await this.client.put(`/table/${id}`, table);
  }

  async updateTableStatus(tableId: string, status: Table['status']): Promise<void> {
    await this.client.patch(`/table/${tableId}/status`, { status });
  }

  async deleteTable(id: string): Promise<void> {
    await this.client.delete(`/table/${id}`);
  }

  async updateTableLayout(layoutUpdates: TableLayoutUpdate[]): Promise<void> {
    await this.client.post('/table/layout/update', layoutUpdates);
  }

  async bulkCreateTables(request: {
    prefix?: string;
    startNumber: number;
    endNumber: number;
    sectionName?: string;
    floorId?: string;
    defaultCapacity?: number;
    shape?: number;
  }): Promise<Table[]> {
    const response = await this.client.post<{ tables: Table[] }>('/table/bulk-create', request);
    return response.data?.tables || [];
  }

  async transferTable(sourceTableId: string, targetTableId: string): Promise<{ message: string; ordersTransferred: number }> {
    const response = await this.client.post<any>(`/table/${sourceTableId}/transfer`, { targetTableId });
    return response.data;
  }

  // Menu management
  private transformMenuCategoryFromBackend(backendCat: any): MenuCategory {
    return {
      id: backendCat.id || backendCat.Id,
      restaurantId: backendCat.restaurantId || backendCat.RestaurantId,
      name: backendCat.name || backendCat.Name,
      description: backendCat.description || backendCat.Description || '',
      displayOrder: backendCat.displayOrder ?? backendCat.DisplayOrder ?? 0,
      isActive: backendCat.isActive !== undefined ? backendCat.isActive : (backendCat.IsActive !== undefined ? backendCat.IsActive : true),
      createdAt: backendCat.createdAt || backendCat.CreatedAt,
      updatedAt: backendCat.updatedAt || backendCat.UpdatedAt,
    } as MenuCategory;
  }

  async getMenuCategories(restaurantId: string): Promise<MenuCategory[]> {
    const cacheKey = `menu-categories:${restaurantId}`;
    try {
      const response = await this.client.get<any[]>(`/menucategory/restaurant/${restaurantId}`);
      const items = response.data || [];
      const transformed = items.map(cat => this.transformMenuCategoryFromBackend(cat));
      await this.setCachedValue(cacheKey, transformed);
      return transformed;
    } catch (error) {
      if (!globalIsOnline || this.isOfflineFriendlyError(error)) {
        return await this.getCachedValue<MenuCategory[]>(cacheKey, []);
      }
      throw error;
    }
  }

  async getMenuCategoryById(id: string): Promise<MenuCategory> {
    const response = await this.client.get<any>(`/menucategory/${id}`);
    return this.transformMenuCategoryFromBackend(response.data);
  }

  async createMenuCategory(category: Partial<MenuCategory>): Promise<MenuCategory> {
    const backendCat: any = {
      Name: category.name,
      Description: category.description,
      DisplayOrder: category.displayOrder,
      IsActive: category.isActive,
      RestaurantId: category.restaurantId
    };
    const response = await this.client.post<any>('/menucategory', backendCat);
    return this.transformMenuCategoryFromBackend(response.data);
  }

  async updateMenuCategory(id: string, category: Partial<MenuCategory>): Promise<void> {
    const backendCat: any = {};
    if (category.name !== undefined) backendCat.Name = category.name;
    if (category.description !== undefined) backendCat.Description = category.description;
    if (category.displayOrder !== undefined) backendCat.DisplayOrder = category.displayOrder;
    if (category.isActive !== undefined) backendCat.IsActive = category.isActive;
    await this.client.put(`/menucategory/${id}`, backendCat);
  }

  async deleteMenuCategory(id: string): Promise<void> {
    await this.client.delete(`/menucategory/${id}`);
  }

  async getMenuItems(categoryId?: string, restaurantId?: string): Promise<MenuItem[]> {
    const cacheKey = categoryId
      ? `menu-items:category:${categoryId}`
      : restaurantId
        ? `menu-items:restaurant:${restaurantId}`
        : '';

    let response;
    try {
      if (categoryId) {
        response = await this.client.get<any[]>(`/menuitem/category/${categoryId}`);
      } else if (restaurantId) {
        // pageSize=2000 ensures the full menu loads in one request (backend default caps at 100)
        response = await this.client.get<any>(`/menuitem/restaurant/${restaurantId}?page=1&pageSize=2000`);
      } else {
        return [];
      }

      const raw = response.data;
      const items: any[] = Array.isArray(raw) ? raw : (raw?.data || []);
      const transformed = items.map(item => this.transformMenuItemFromBackend(item));
      if (cacheKey) {
        await this.setCachedValue(cacheKey, transformed);
      }
      return transformed;
    } catch (error) {
      if ((!globalIsOnline || this.isOfflineFriendlyError(error)) && cacheKey) {
        return await this.getCachedValue<MenuItem[]>(cacheKey, []);
      }
      throw error;
    }
  }

  async searchMenuItems(restaurantId: string, term: string): Promise<MenuItem[]> {
    const response = await this.client.get<any[]>(`/menuitem/search/${restaurantId}?term=${encodeURIComponent(term)}`);
    const items = response.data || [];
    // Transform backend menu items to frontend format
    return items.map(item => this.transformMenuItemFromBackend(item));
  }

  async getMenuItemById(id: string): Promise<MenuItem> {
    const response = await this.client.get<any>(`/menuitem/${id}`);
    return this.transformMenuItemFromBackend(response.data);
  }

  async createMenuItem(item: Partial<MenuItem>): Promise<MenuItem> {
    let tagList: string[] = [];
    if (item.tags) {
      const rawTags: any = item.tags;
      if (Array.isArray(rawTags)) {
        tagList = [...rawTags];
      } else if (typeof rawTags === 'string') {
        tagList = (rawTags as string).split(',').map(t => t.trim());
      }
    }
    if (item.isVegetarian && !tagList.includes('Vegetarian')) tagList.push('Vegetarian');
    if (!item.isVegetarian && tagList.includes('Vegetarian')) tagList = tagList.filter(t => t !== 'Vegetarian');
    if (item.isSpicy && !tagList.includes('Spicy')) tagList.push('Spicy');
    if (!item.isSpicy && tagList.includes('Spicy')) tagList = tagList.filter(t => t !== 'Spicy');

    const backendItem: any = {
      MenuCategoryId: item.categoryId,
      RestaurantId: (item as any).restaurantId,
      Name: item.name,
      ShortcutName: item.shortcutName || null,
      Description: item.description,
      Price: item.price,
      IsAvailable: item.isAvailable,
      Tags: tagList.join(','),
      Sizes: item.sizes && item.sizes.length ? JSON.stringify(item.sizes) : null,
      IsHappyHourEnabled: item.isHappyHourEnabled || false,
      HappyHourPrice: item.happyHourPrice || null,
      HappyHourStart: item.happyHourStart || null,
      HappyHourEnd: item.happyHourEnd || null,
      HappyHourDays: item.happyHourDays || null,
      DisplayOrder: 0,
    };
    
    const response = await this.client.post<MenuItem>('/menuitem', backendItem);
    return response.data;
  }

  async updateMenuItem(id: string, item: Partial<MenuItem>): Promise<void> {
    const backendItem: any = {};
    
    if (item.categoryId !== undefined) backendItem.MenuCategoryId = item.categoryId;
    if (item.name !== undefined) backendItem.Name = item.name;
    if (item.shortcutName !== undefined) backendItem.ShortcutName = item.shortcutName || null;
    if (item.description !== undefined) backendItem.Description = item.description;
    if (item.price !== undefined) backendItem.Price = item.price;
    if (item.isAvailable !== undefined) backendItem.IsAvailable = item.isAvailable;
    
    // Handle tags combined with Vegetarian and Spicy flags
    let tagList: string[] = [];
    if (item.tags) {
      const rawTags: any = item.tags;
      if (Array.isArray(rawTags)) {
        tagList = [...rawTags];
      } else if (typeof rawTags === 'string') {
        tagList = (rawTags as string).split(',').map(t => t.trim());
      }
    }
    if (item.isVegetarian !== undefined) {
      if (item.isVegetarian && !tagList.includes('Vegetarian')) tagList.push('Vegetarian');
      if (!item.isVegetarian && tagList.includes('Vegetarian')) tagList = tagList.filter(t => t !== 'Vegetarian');
    }
    if (item.isSpicy !== undefined) {
      if (item.isSpicy && !tagList.includes('Spicy')) tagList.push('Spicy');
      if (!item.isSpicy && tagList.includes('Spicy')) tagList = tagList.filter(t => t !== 'Spicy');
    }
    if (item.tags !== undefined || item.isVegetarian !== undefined || item.isSpicy !== undefined) {
      backendItem.Tags = tagList.join(',');
    }
    
    if (item.sizes !== undefined) backendItem.Sizes = item.sizes && item.sizes.length ? JSON.stringify(item.sizes) : null;

    if (item.isHappyHourEnabled !== undefined) backendItem.IsHappyHourEnabled = item.isHappyHourEnabled;
    if ((item as any).happyHourPrice !== undefined) backendItem.HappyHourPrice = (item as any).happyHourPrice;
    if ((item as any).happyHourStart !== undefined) backendItem.HappyHourStart = (item as any).happyHourStart;
    if ((item as any).happyHourEnd !== undefined) backendItem.HappyHourEnd = (item as any).happyHourEnd;
    if ((item as any).happyHourDays !== undefined) backendItem.HappyHourDays = (item as any).happyHourDays;
    
    await this.client.put(`/menuitem/${id}`, backendItem);
  }

  async scanMenuImage(file: File): Promise<Array<{ category: string; name: string; price: number; description: string; isVegetarian?: boolean; isSpicy?: boolean; sizeName?: string | null; tag?: string | null }>> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post<{ items: any[]; count: number }>('/menuitem/scan-menu', formData, {
      headers: { 'Content-Type': undefined },
      timeout: 90000,
    });
    return response.data?.items || [];
  }

  async uploadMenuItemImage(id: string, file: File): Promise<{ imageUrl: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post<{ imageUrl: string }>(
      `/menuitem/upload-image/${id}`,
      formData,
      { headers: { 'Content-Type': undefined } }
    );
    return response.data;
  }

  async deleteMenuItem(id: string): Promise<void> {
    await this.client.delete(`/menuitem/${id}`);
  }

  // ── Inventory ──────────────────────────────────────────────────────────────

  async getMeasurementUnits(): Promise<{ id: number; name: string; symbol: string; category: string; sortOrder: number }[]> {
    const r = await this.client.get<any[]>('/inventory/units');
    return r.data || [];
  }

  async getInventoryDashboard(restaurantId: string) {
    const r = await this.client.get<any>(`/inventory/dashboard/restaurant/${restaurantId}`);
    return r.data;
  }

  async getIngredients(restaurantId: string, category?: string): Promise<any[]> {
    let url = `/inventory/ingredients/restaurant/${restaurantId}`;
    if (category) url += `?category=${encodeURIComponent(category)}`;
    const r = await this.client.get<any[]>(url);
    return r.data || [];
  }

  async createIngredient(payload: {
    restaurantId: string; name: string; unit: string;
    currentStock: number; minStockLevel: number; reorderPoint: number;
    costPerUnit: number; category?: string; expiryDate?: string;
  }): Promise<any> {
    const r = await this.client.post<any>('/inventory/ingredients', payload);
    return r.data;
  }

  async adjustStock(ingredientId: string, quantity: number, notes?: string): Promise<any> {
    const r = await this.client.post<any>(`/inventory/ingredients/${ingredientId}/adjust`, { quantity, notes });
    return r.data;
  }

  async getVendors(restaurantId: string): Promise<any[]> {
    const r = await this.client.get<any[]>(`/inventory/vendors/restaurant/${restaurantId}`);
    return r.data || [];
  }

  async createVendor(payload: {
    restaurantId: string; name: string; contactPerson?: string;
    phone?: string; email?: string; address?: string; gstNumber?: string; paymentTerms?: string;
  }): Promise<any> {
    const r = await this.client.post<any>('/inventory/vendors', payload);
    return r.data;
  }

  async updateVendor(id: string, payload: Partial<{ name: string; contactPerson: string; phone: string; email: string; address: string; gstNumber: string; }>): Promise<any> {
    const r = await this.client.put<any>(`/inventory/vendors/${id}`, payload);
    return r.data;
  }

  async getPurchaseOrders(restaurantId: string): Promise<any[]> {
    const r = await this.client.get<any[]>(`/inventory/purchase-orders/restaurant/${restaurantId}`);
    return r.data || [];
  }

  async createPurchaseOrder(payload: {
    restaurantId: string; vendorId: string; expectedDate?: string;
    taxAmount?: number; notes?: string;
    items: { ingredientId: string; quantity: number; unit: string; unitPrice: number }[];
  }): Promise<any> {
    const r = await this.client.post<any>('/inventory/purchase-orders', payload);
    return r.data;
  }

  async getWasteEntries(restaurantId: string): Promise<any[]> {
    const r = await this.client.get<any[]>(`/inventory/waste/restaurant/${restaurantId}`);
    return r.data || [];
  }

  async recordWaste(payload: {
    restaurantId: string; ingredientId?: string; menuItemId?: string;
    quantity: number; unit: string; estimatedCost: number;
    reason: string; notes?: string; deductFromStock?: boolean;
  }): Promise<any> {
    const r = await this.client.post<any>('/inventory/waste', { deductFromStock: true, ...payload });
    return r.data;
  }

  // ── Inventory: recipes, PO receive, movements, low-stock ─────────────────

  async getRecipe(menuItemId: string): Promise<any[]> {
    const r = await this.client.get<any[]>(`/inventory/recipe/${menuItemId}`);
    return r.data || [];
  }

  async addRecipeIngredient(data: { menuItemId: string; ingredientId: string; quantity: number; unit: string }): Promise<any> {
    const r = await this.client.post<any>('/inventory/recipe', data);
    return r.data;
  }

  async deleteRecipeIngredient(id: string): Promise<void> {
    await this.client.delete(`/inventory/recipe/${id}`);
  }

  async updateIngredient(id: string, data: Partial<{ name: string; unit: string; minStockLevel: number; reorderPoint: number; costPerUnit: number; category: string; expiryDate: string }>): Promise<any> {
    const r = await this.client.put<any>(`/inventory/ingredients/${id}`, data);
    return r.data;
  }

  async getLowStockIngredients(restaurantId: string): Promise<any[]> {
    const r = await this.client.get<any[]>(`/inventory/ingredients/restaurant/${restaurantId}/low-stock`);
    return r.data || [];
  }

  async getExpiringIngredients(restaurantId: string, days = 7): Promise<any[]> {
    const r = await this.client.get<any[]>(`/inventory/ingredients/restaurant/${restaurantId}/expiring?days=${days}`);
    return r.data || [];
  }

  async getPurchaseOrderDetail(poId: string): Promise<any> {
    const r = await this.client.get<any>(`/inventory/purchase-orders/${poId}`);
    return r.data;
  }

  async updatePOStatus(poId: string, status: number): Promise<any> {
    const r = await this.client.put<any>(`/inventory/purchase-orders/${poId}/status`, { status });
    return r.data;
  }

  async receivePurchaseOrder(poId: string, items: { purchaseOrderItemId: string; receivedQuantity: number }[]): Promise<any> {
    const r = await this.client.post<any>(`/inventory/purchase-orders/${poId}/receive`, { items });
    return r.data;
  }

  async getStockMovements(restaurantId: string, take = 100): Promise<any[]> {
    const r = await this.client.get<any[]>(`/inventory/stock-movements/restaurant/${restaurantId}?take=${take}`);
    return r.data || [];
  }

  // Order management
  async createOrder(orderData: Partial<Order>): Promise<Order> {
    if (!globalIsOnline) {
      return await this.saveOfflineOrder(orderData);
    }

    try {
      const response = await this.client.post<any>('/orders', orderData);
      const d = response.data;
      if (d?.success !== undefined) {
        if (d.success && d.data) return d.data as Order;
        throw new Error(d.message || 'Failed to create order');
      }
      if (d?.id) return d as Order;
      throw new Error('Failed to create order');
    } catch (error) {
      if (this.isOfflineFriendlyError(error)) {
        return await this.saveOfflineOrder(orderData);
      }
      throw error;
    }
  }

  async getOrders(restaurantId: string, opts?: { status?: number | string }): Promise<Order[]> {
    let url = `/orders/restaurant/${restaurantId}?includeItems=true`;
    if (opts?.status !== undefined) {
      url += `&status=${opts.status}`;
    }
    try {
      const response = await this.client.get<any>(url);
      const online = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      // merge with any local offline orders not yet synced
      const localOrders = await localDb.getPendingOrders();
      const localForRestaurant = localOrders.filter((o: any) => o.restaurantId === restaurantId && o.id?.startsWith('offline-'));
      const localIds = new Set(localForRestaurant.map((o: any) => o.id));
      const merged = [...online.filter((o: any) => !localIds.has(o.id)), ...localForRestaurant];
      return merged;
    } catch (error) {
      if (!globalIsOnline || this.isOfflineFriendlyError(error)) {
        const localOrders = await localDb.getPendingOrders();
        return localOrders.filter((o: any) => o.restaurantId === restaurantId) as Order[];
      }
      throw error;
    }
  }

  async getOrderById(id: string): Promise<Order> {
    if (id.startsWith('offline-')) {
      const localOrder = await localDb.getPendingOrderById(id);
      if (localOrder) return localOrder as Order;
    }

    try {
      const response = await this.client.get<any>(`/orders/${id}`);
      const d = response.data;
      if (d?.success !== undefined) {
        if (d.success && d.data) return d.data as Order;
        throw new Error(d.message || 'Order not found');
      }
      if (d && (d.id || d.orderNumber)) return d as Order;
      throw new Error('Order not found');
    } catch (error) {
      if (this.isOfflineFriendlyError(error)) {
        const localOrder = await localDb.getPendingOrderById(id);
        if (localOrder) return localOrder as Order;
      }
      throw error;
    }
  }

  async updateOrderStatus(orderId: string, status: Order['status']): Promise<any> {
    // For offline orders — update the local record only
    if (orderId.startsWith('offline-')) {
      const order = await localDb.getPendingOrderById(orderId);
      if (order) {
        order.status = status;
        order.updatedAt = new Date();
        await localDb.addPendingOrder(order);
      }
      return { success: true };
    }

    try {
      const response = await this.client.patch(`/orders/${orderId}/status`, { status });
      return response.data;
    } catch (error) {
      if (!globalIsOnline || this.isOfflineFriendlyError(error)) {
        // Queue the status update so it syncs when back online
        await localDb.addOrderUpdate({
          id: `update-${orderId}-${Date.now()}`,
          orderId,
          status,
          updatedAt: new Date().toISOString(),
        });
        return { success: true, queued: true };
      }
      throw error;
    }
  }

  async approveOrder(orderId: string): Promise<any> {
    return this.updateOrderStatus(orderId, 'Confirmed' as Order['status']);
  }

  async getKitchenOrders(restaurantId: string): Promise<Order[]> {
    const CACHE_KEY = `kitchen-orders:${restaurantId}`;
    try {
      const response = await this.client.get<any>(`/orders/restaurant/${restaurantId}/kitchen`);
      const orders = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      // Persist snapshot so KDS/POS can work offline with the last-known order list
      await localDb.setCache(CACHE_KEY, orders).catch(() => {});
      return orders;
    } catch (error) {
      if (!globalIsOnline || this.isOfflineFriendlyError(error)) {
        const [cached, pendingOrders, pendingUpdates] = await Promise.all([
          localDb.getCache(CACHE_KEY),
          localDb.getPendingOrders(),
          localDb.getPendingOrderUpdates(),
        ]);

        // Apply any status changes made while offline to the cached snapshot
        // so KDS shows the correct stage even without a server round-trip
        let serverOrders: any[] = cached ?? [];
        if (pendingUpdates.length > 0) {
          const statusMap = new Map<string, string>(
            (pendingUpdates as any[]).map(u => [u.orderId, u.status])
          );
          serverOrders = serverOrders.map(o =>
            statusMap.has(o.id) ? { ...o, status: statusMap.get(o.id) } : o
          );
        }

        // Merge locally-created offline orders (they take priority; dedup by id)
        const offlineOrders = pendingOrders.filter((o: OfflineOrderQueueEntry) => o.restaurantId === restaurantId);
        const seen = new Set(offlineOrders.map((o: any) => o.id));
        return [...offlineOrders, ...serverOrders.filter((o: any) => !seen.has(o.id))];
      }
      throw error;
    }
  }

  async getPendingOrders(restaurantId: string): Promise<Order[]> {
    const response = await this.client.get<any>(`/orders/restaurant/${restaurantId}/pending`);
    return Array.isArray(response.data) ? response.data : (response.data?.data || []);
  }

  async getRestaurantRevenue(restaurantId: string, startDate?: string, endDate?: string): Promise<number> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await this.client.get<any>(`/Order/restaurant/${restaurantId}/revenue`, { params });
    return response.data?.revenue ?? 0;
  }

  async getRestaurantOrderCount(restaurantId: string): Promise<number> {
    const response = await this.client.get<any>(`/Order/restaurant/${restaurantId}/count`);
    return response.data?.count ?? 0;
  }

  async addOrderItem(orderId: string, itemData: Partial<OrderItem>): Promise<OrderItem> {
    const response = await this.client.post<ApiResponse<OrderItem>>(`/orders/${orderId}/items`, itemData);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to add item to order');
  }

  // POS specific - Note: getActiveTables is already defined above in Table management section
  async printKOT(orderId: string, printerIds?: string[]): Promise<any> {
    if (!globalIsOnline) {
      // Queue the KOT job so it prints when back online
      const order = await localDb.getPendingOrderById(orderId);
      await localDb.addKOTJob({
        id: `kot-${orderId}-${Date.now()}`,
        orderId,
        orderNumber: order?.orderNumber || orderId,
        restaurantId: order?.restaurantId || '',
        printerIds,
        queuedAt: new Date().toISOString(),
      });
      return { success: true, queued: true, message: 'KOT queued — will print when back online' };
    }
    try {
      const response = await this.client.post('/printer/print-kot', { orderId, printerIds });
      return response.data;
    } catch (error) {
      if (this.isOfflineFriendlyError(error)) {
        const order = await localDb.getPendingOrderById(orderId);
        await localDb.addKOTJob({
          id: `kot-${orderId}-${Date.now()}`,
          orderId,
          orderNumber: order?.orderNumber || orderId,
          restaurantId: order?.restaurantId || '',
          printerIds,
          queuedAt: new Date().toISOString(),
        });
        return { success: true, queued: true, message: 'KOT queued — will print when back online' };
      }
      throw error;
    }
  }

  async getKOTConfig(): Promise<KOTConfig> {
    const response = await this.client.get<KOTConfig>('/printer/kot-config');
    return response.data;
  }

  async saveKOTConfig(config: KOTConfig): Promise<KOTConfig> {
    const response = await this.client.post<KOTConfig>('/printer/kot-config', config);
    return response.data;
  }

  async processPayment(orderId: string, paymentData: {
    method: number;       // 1=Cash, 2=Card, 3=UPI
    amount: number;
    tenderedAmount?: number;
    transactionId?: string;
  }): Promise<any> {
    if (!globalIsOnline) {
      return await this.recordOfflinePayment(orderId, paymentData);
    }
    try {
      const response = await this.client.post('/payment', {
        orderId,
        method: paymentData.method,
        amount: paymentData.amount,
        tipAmount: 0,
        discountAmount: 0,
        taxAmount: 0,
        tenderedAmount: paymentData.tenderedAmount ?? null,
        transactionReference: paymentData.transactionId ?? null,
      });
      return response.data;
    } catch (error) {
      if (this.isOfflineFriendlyError(error)) {
        return await this.recordOfflinePayment(orderId, paymentData);
      }
      throw error;
    }
  }

  async processSplitPayment(orderId: string, splitData: {
    amount: number;
    tipAmount?: number;
    taxAmount?: number;
    discountAmount?: number;
    notes?: string;
    splits: {
      method: number; // 1=Cash, 2=Card, 3=UPI, etc.
      amount: number;
      gatewayName?: string;
    }[];
  }): Promise<any> {
    if (!globalIsOnline) {
      return await this.recordOfflineSplitPayment(orderId, splitData);
    }
    try {
      const response = await this.client.post('/payment/split', {
        orderId,
        amount: splitData.amount,
        tipAmount: splitData.tipAmount ?? 0,
        taxAmount: splitData.taxAmount ?? 0,
        discountAmount: splitData.discountAmount ?? 0,
        notes: splitData.notes ?? null,
        splits: splitData.splits.map(s => ({
          method: s.method,
          amount: s.amount,
          gatewayName: s.gatewayName ?? null
        }))
      });
      return response.data;
    } catch (error) {
      if (this.isOfflineFriendlyError(error)) {
        return await this.recordOfflineSplitPayment(orderId, splitData);
      }
      throw error;
    }
  }

  private async recordOfflinePayment(orderId: string, paymentData: {
    method: number;
    amount: number;
    tenderedAmount?: number;
    transactionId?: string;
  }): Promise<any> {
    const pendingOrders = await localDb.getPendingOrders();
    const order = pendingOrders.find((o: OfflineOrderQueueEntry) => o.id === orderId) as OfflineOrderQueueEntry | undefined;
    if (order) {
      order.paymentStatus = 'Paid';
      order.paymentMethod = paymentData.method as any;
      order.paidAmount = paymentData.amount;
      (order as any).paymentTenderedAmount = paymentData.tenderedAmount ?? null;
      (order as any).paymentTransactionReference = paymentData.transactionId ?? null;
      order.paymentPayload = { ...paymentData };
      order.updatedAt = new Date();
      await localDb.addPendingOrder(order);
    }
    return { success: true, message: 'Payment recorded offline.' };
  }

  private async recordOfflineSplitPayment(orderId: string, splitData: {
    amount: number;
    tipAmount?: number;
    taxAmount?: number;
    discountAmount?: number;
    notes?: string;
    splits: {
      method: number;
      amount: number;
      gatewayName?: string;
    }[];
  }): Promise<any> {
    const pendingOrders = await localDb.getPendingOrders();
    const order = pendingOrders.find((o: OfflineOrderQueueEntry) => o.id === orderId) as OfflineOrderQueueEntry | undefined;
    if (order) {
      order.paymentStatus = 'Paid';
      order.splitPayments = splitData.splits.map((split) => ({ ...split }));
      order.paidAmount = splitData.amount;
      order.updatedAt = new Date();
      await localDb.addPendingOrder(order);
    }
    return { success: true, message: 'Split payment recorded offline.' };
  }

  async syncPendingOfflineOrders(): Promise<{ synced: number; failed: number }> {
    if (!globalIsOnline) {
      return { synced: 0, failed: 0 };
    }

    if (this.syncInFlight) {
      return this.syncInFlight;
    }

    this.syncInFlight = (async () => {
      const pendingOrders = await localDb.getPendingOrders() as OfflineOrderQueueEntry[];
      const syncableOrders = pendingOrders.filter((order) => order.id?.startsWith('offline-'));
      let synced = 0;
      let failed = 0;

      for (const order of syncableOrders) {
        try {
          order.syncStatus = 'syncing';
          order.syncError = null;
          await localDb.addPendingOrder(order);

          const createPayload = {
            restaurantId: order.restaurantId,
            tableId: order.tableId || undefined,
            userId: order.userId || undefined,
            subtotal: order.subtotal ?? 0,
            taxAmount: order.taxAmount ?? 0,
            discountAmount: order.discountAmount ?? 0,
            totalAmount: order.totalAmount ?? 0,
            notes: order.notes || '',
            orderType: order.orderType ?? 1,
            items: (order.items || order.orderItems || []).map((item: any) => ({
              menuItemId: item.menuItemId,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              totalPrice: item.totalPrice,
              specialInstructions: item.specialInstructions || undefined,
            })),
          };

          const createdOrder = await this.createOnlineOrderForSync(createPayload);

          if (order.splitPayments && order.splitPayments.length > 0) {
            await this.client.post('/payment/split', {
              orderId: createdOrder.id,
              amount: order.paidAmount ?? order.totalAmount ?? 0,
              tipAmount: 0,
              taxAmount: 0,
              discountAmount: 0,
              notes: 'Offline POS split payment sync',
              splits: order.splitPayments.map((split) => ({
                method: split.method,
                amount: split.amount,
                gatewayName: split.gatewayName ?? null,
              })),
            });
          } else if (order.paymentPayload) {
            await this.client.post('/payment', {
              orderId: createdOrder.id,
              method: order.paymentPayload.method,
              amount: order.paymentPayload.amount,
              tipAmount: 0,
              discountAmount: 0,
              taxAmount: 0,
              tenderedAmount: order.paymentPayload.tenderedAmount ?? null,
              transactionReference: order.paymentPayload.transactionId ?? null,
            });
          }

          synced += 1;
          await localDb.removePendingOrder(order.id);

          // Remap any queued KOT jobs that still carry the offline ID.
          // Without this, the KOT drain below sends the offline-xxx ID to the
          // server which 404s — the KOT never prints.
          const allKots = await localDb.getPendingKOTJobs();
          for (const kot of (allKots as any[]).filter(k => k.orderId === order.id)) {
            await localDb.removeKOTJob(kot.id);
            await localDb.addKOTJob({ ...kot, id: `kot-${createdOrder.id}-synced`, orderId: createdOrder.id });
          }
        } catch (error: any) {
          failed += 1;
          order.syncStatus = 'failed';
          order.syncError = error?.response?.data?.message || error?.message || 'Unknown sync error';
          order.updatedAt = new Date();
          await localDb.addPendingOrder(order);
        }
      }

      // ── Sync queued order status updates ─────────────────────────────────
      const pendingUpdates = await localDb.getPendingOrderUpdates();
      for (const update of pendingUpdates) {
        try {
          await this.client.patch(`/orders/${update.orderId}/status`, { status: update.status });
          await localDb.removeOrderUpdate(update.id);
        } catch {
          // leave in queue for next sync cycle
        }
      }

      // ── Drain KOT print queue ─────────────────────────────────────────────
      const pendingKOTs = await localDb.getPendingKOTJobs();
      for (const job of pendingKOTs) {
        try {
          await this.client.post('/printer/print-kot', { orderId: job.orderId, printerIds: job.printerIds });
          await localDb.removeKOTJob(job.id);
        } catch {
          // leave in queue — printer may not be ready yet
        }
      }

      return { synced, failed };
    })();

    try {
      return await this.syncInFlight;
    } finally {
      this.syncInFlight = null;
    }
  }

  private async createOnlineOrderForSync(orderData: Partial<Order>): Promise<Order> {
    const response = await this.client.post<any>('/orders', orderData);
    const d = response.data;
    if (d?.success !== undefined) {
      if (d.success && d.data) return d.data as Order;
      throw new Error(d.message || 'Failed to sync offline order');
    }
    if (d?.id) return d as Order;
    throw new Error('Failed to sync offline order');
  }

  // Billing lifecycle endpoints
  async generateBill(orderId: string, request: { discountType: string | null; discountValue: number; discountReason: string | null; applyServiceCharge: boolean }): Promise<any> {
    const response = await this.client.post<any>(`/orders/${orderId}/bill`, request);
    return response.data;
  }

  async processBillPayment(billId: number, request: { payments: Array<{ paymentMode: string; amount: number; transactionRef?: string | null }> }): Promise<any> {
    const response = await this.client.post<any>(`/bills/${billId}/payment`, request);
    return response.data;
  }

  async getBillDetails(billId: number): Promise<any> {
    const response = await this.client.get<any>(`/bills/${billId}`);
    return response.data;
  }

  async getBillByOrderId(orderId: string): Promise<any> {
    const response = await this.client.get<any>(`/orders/${orderId}/bill`);
    return response.data;
  }

  async cancelBill(billId: number, reason: string): Promise<any> {
    const response = await this.client.put<any>(`/bills/${billId}/cancel`, { reason });
    return response.data;
  }

  async reprintBill(billId: number): Promise<any> {
    const response = await this.client.post<any>(`/bills/${billId}/reprint`);
    return response.data;
  }

  async sendWhatsAppReceipt(billId: number, phone: string): Promise<any> {
    const response = await this.client.post<any>(`/bills/${billId}/whatsapp?phone=${encodeURIComponent(phone)}`);
    return response.data;
  }

  async sendEmailReceipt(billId: number, email: string): Promise<any> {
    const response = await this.client.post<any>(`/bills/${billId}/email?email=${encodeURIComponent(email)}`);
    return response.data;
  }

  // GST / Billing Config
  async getGstConfig(): Promise<{ cgst: number; sgst: number; igst: number; serviceChargePercent: number; serviceChargeEnabled: boolean; defaultDiscountType: string | null; defaultDiscountValue: number }> {
    const response = await this.client.get<any>('/gst/config');
    const d = response.data;
    return {
      cgst: d.cgst ?? d.CGST ?? 2.5,
      sgst: d.sgst ?? d.SGST ?? 2.5,
      igst: d.igst ?? d.IGST ?? 5,
      serviceChargePercent: d.serviceChargePercent ?? d.ServiceChargePercent ?? 5,
      serviceChargeEnabled: d.serviceChargeEnabled ?? d.ServiceChargeEnabled ?? false,
      defaultDiscountType: d.defaultDiscountType ?? d.DefaultDiscountType ?? null,
      defaultDiscountValue: d.defaultDiscountValue ?? d.DefaultDiscountValue ?? 0,
    };
  }

  // User management
  async getUsers(tenantId?: string, restaurantId?: string): Promise<User[]> {
    const params = new URLSearchParams();
    if (tenantId) params.append('tenantId', tenantId);
    if (restaurantId) params.append('restaurantId', restaurantId);

    const url = `/User${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await this.client.get<any>(url);
    // Backend returns array directly (not wrapped in ApiResponse)
    const backendUsers = Array.isArray(response.data) ? response.data : (response.data?.data || []);
    return backendUsers.map((user: any) => this.transformUserFromBackend(user));
  }

  async createUser(userData: {
    tenantId: string;
    restaurantId?: string;
    username: string;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    role: string;
  }): Promise<User> {
    // Transform frontend data to match backend UserCreateRequest
    const backendData = {
      TenantId: userData.tenantId,
      Username: userData.username,
      Email: userData.email,
      Password: userData.password,
      FullName: `${userData.firstName} ${userData.lastName}`.trim(),
      Phone: "", // Default empty phone
      Role: userData.role as any, // Cast to UserRole enum
      IsActive: true // Default to active
    };
    
    // Backend returns user object directly (201 Created), not wrapped in ApiResponse
    const response = await this.client.post<any>('/User', backendData);
    const createdUserData = response.data?.success !== undefined ? response.data.data : response.data;
    if (!createdUserData) throw new Error(response.data?.message || 'Failed to create user');
    return this.transformUserFromBackend(createdUserData);
  }

  async updateUser(userId: string, data: { firstName?: string; lastName?: string; email?: string; username?: string; phone?: string; password?: string; role?: string; isActive?: boolean; tenantId?: string }): Promise<User> {
    const backendData: any = {};
    if (data.firstName !== undefined || data.lastName !== undefined) {
      backendData.FullName = `${data.firstName || ''} ${data.lastName || ''}`.trim();
    }
    if (data.email !== undefined) backendData.Email = data.email;
    if (data.username !== undefined) backendData.Username = data.username;
    if (data.phone !== undefined) backendData.Phone = data.phone;
    if (data.password !== undefined && data.password !== '') backendData.Password = data.password;
    if (data.role !== undefined) backendData.Role = data.role as any;
    if (data.isActive !== undefined) backendData.IsActive = data.isActive;
    if (data.tenantId !== undefined) backendData.TenantId = data.tenantId;

    const response = await this.client.put<any>(`/User/${userId}`, backendData);
    const updatedUserData = response.data?.success !== undefined ? response.data.data : response.data;
    if (!updatedUserData) throw new Error(response.data?.message || 'Failed to update user');
    return this.transformUserFromBackend(updatedUserData);
  }

  async updateUserStatus(userId: string, status: string): Promise<User> {
    const isActive = status === 'Active';
    const response = await this.client.patch<any>(`/User/${userId}/status`, { isActive });
    const updatedUserData = response.data?.success !== undefined ? response.data.data : response.data;
    if (!updatedUserData) throw new Error(response.data?.message || 'Failed to update user status');
    return this.transformUserFromBackend(updatedUserData);
  }

  async updateTenantStatus(tenantId: string, isActive: boolean): Promise<Tenant> {
    // Fixed URL: /tenant/ (singular) matches backend route api/Tenant/{id}/status
    const response = await this.client.patch<any>(`/tenant/${tenantId}/status`, { isActive });
    // Backend returns tenant directly, not wrapped
    if (response.data?.success !== undefined) {
      if (response.data.success && response.data.data) return response.data.data;
      throw new Error(response.data.message || 'Failed to update tenant status');
    }
    return response.data;
  }

  async deleteUser(userId: string): Promise<void> {
    const response = await this.client.delete<any>(`/User/${userId}`);
    const success = response.data?.success !== undefined ? response.data.success : (response.status === 200);
    if (!success) {
      throw new Error(response.data?.message || 'Failed to delete user');
    }
  }

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.client.get('/health');
      return response.status === 200;
    } catch {
      return false;
    }
  }

  // Pricing Plan management (SuperAdmin)
  async getPricingPlans(): Promise<PricingPlan[]> {
    const response = await this.client.get<PricingPlan[]>('/pricingplan');
    return response.data || [];
  }

  async getAllPricingPlans(): Promise<PricingPlan[]> {
    const response = await this.client.get<PricingPlan[]>('/pricingplan/all');
    return response.data || [];
  }

  async checkModuleAccess(restaurantId: string): Promise<any> {
    const response = await this.client.get(`/pricingplan/check-access/${restaurantId}`);
    return response.data;
  }

  async getPricingPlanById(id: string): Promise<PricingPlan> {
    const response = await this.client.get<PricingPlan>(`/pricingplan/${id}`);
    return response.data;
  }

  async createPricingPlan(plan: PricingPlan): Promise<PricingPlan> {
    const response = await this.client.post<PricingPlan>('/pricingplan', plan);
    return response.data;
  }

  async updatePricingPlan(id: string, plan: PricingPlan): Promise<PricingPlan> {
    const response = await this.client.put<PricingPlan>(`/pricingplan/${id}`, plan);
    return response.data;
  }

  async deletePricingPlan(id: string): Promise<{ message: string }> {
    const response = await this.client.delete<{ message: string }>(`/pricingplan/${id}`);
    return response.data;
  }

  async duplicatePricingPlan(id: string): Promise<PricingPlan> {
    const response = await this.client.post<PricingPlan>(`/pricingplan/${id}/duplicate`);
    return response.data;
  }

  // Subscription Payments
  async getSubscriptionPayments(): Promise<SubscriptionPayment[]> {
    const response = await this.client.get<SubscriptionPayment[]>('/subscriptionpayment');
    return response.data || [];
  }

  async createSubscriptionPayment(payload: {
    tenantId: string;
    pricingPlanId: string;
    amount: number;
    billingCycle: string;
    paymentMethod: string;
    transactionId?: string;
  }): Promise<SubscriptionPayment> {
    const response = await this.client.post<SubscriptionPayment>('/subscriptionpayment', payload);
    return response.data;
  }

  async getSubscriptionPaymentsByTenant(tenantId: string): Promise<SubscriptionPayment[]> {
    const response = await this.client.get<SubscriptionPayment[]>(`/subscriptionpayment/tenant/${tenantId}`);
    return response.data || [];
  }


  async updatePassword(userId: string, newPassword: string): Promise<void> {
    try {
      await this.client.post(`/user/${userId}/password`, { newPassword });
    } catch (error) {
      if (isDev) console.error('Failed to update password:', error);
      throw error;
    }
  }

  async getReports(category?: number): Promise<ReportTemplate[]> {
    const query = category !== undefined ? `?category=${category}` : '';
    const response = await this.client.get<ReportTemplate[]>(`/report${query}`);
    return response.data || [];
  }

  async createReport(report: Partial<ReportTemplate>): Promise<ReportTemplate> {
    const response = await this.client.post<ReportTemplate>('/report', report);
    return response.data;
  }

  async executeReport(reportId: string, format: number = 1): Promise<ReportExecution> {
    const response = await this.client.post<ReportExecution>(`/report/${reportId}/execute?format=${format}`);
    return response.data;
  }

  async deleteReport(reportId: string): Promise<void> {
    await this.client.delete(`/report/${reportId}`);
  }

  async getDashboardSnapshot(_restaurantId?: string): Promise<DashboardSnapshot> {
    const response = await this.client.get<DashboardSnapshot>('/report/dashboard');
    return response.data;
  }

  async adjustCashSales(period: string, adjustmentType: string, value: number): Promise<any> {
    const response = await this.client.post('/report/adjust-sales', { period, adjustmentType, value });
    return response.data;
  }

  async getSalesAnalytics(_restaurantId: string, startDate: string, endDate: string): Promise<any> {
    const response = await this.client.get(`/report/analytics/sales?startDate=${startDate}&endDate=${endDate}`);
    return response.data;
  }

  async getTopSellingItems(_restaurantId: string, startDate: Date | string, endDate: Date | string, limit: number = 10): Promise<any> {
    const response = await this.client.get(`/report/analytics/top-items?startDate=${startDate}&endDate=${endDate}&limit=${limit}`);
    return response.data;
  }

  async getTableAnalytics(_restaurantId: string, startDate: string, endDate: string): Promise<any> {
    const response = await this.client.get(`/report/analytics/tables?startDate=${startDate}&endDate=${endDate}`);
    return response.data;
  }

  async getHourlySales(_restaurantId: string, date: string): Promise<any> {
    const response = await this.client.get(`/report/analytics/hourly?date=${date}`);
    return response.data;
  }

  // ── Staff Management ────────────────────────────────────────────────────────

  async getStaffRoster(restaurantId: string): Promise<any[]> {
    const response = await this.client.get<any[]>(`/staff/roster/${restaurantId}`);
    return response.data || [];
  }

  async updateStaffPosition(userId: string, position: string | null): Promise<void> {
    await this.client.patch(`/staff/${userId}/position`, { position });
  }

  async getStaffRoles(): Promise<any[]> {
    const response = await this.client.get<any[]>('/staff/roles');
    return response.data || [];
  }

  async getStaffPermissions(): Promise<any[]> {
    const response = await this.client.get<any[]>('/staff/permissions');
    return response.data || [];
  }

  async setRolePermissions(roleId: string, permissionIds: string[]): Promise<void> {
    await this.client.put(`/staff/roles/${roleId}/permissions`, { permissionIds });
  }

  async getTodayAttendance(restaurantId: string): Promise<any[]> {
    const response = await this.client.get<any[]>(`/staff/attendance/today/${restaurantId}`);
    return response.data || [];
  }

  async getUserAttendance(userId: string, from?: string, to?: string): Promise<any[]> {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const q = params.toString() ? `?${params.toString()}` : '';
    const response = await this.client.get<any[]>(`/staff/${userId}/attendance${q}`);
    return response.data || [];
  }

  async clockIn(userId: string, restaurantId: string, notes?: string): Promise<any> {
    const response = await this.client.post('/staff/clock-in', { userId, restaurantId, notes });
    return response.data;
  }

  async clockOut(userId: string, notes?: string): Promise<any> {
    const response = await this.client.post('/staff/clock-out', { userId, notes });
    return response.data;
  }

  async getStaffShifts(restaurantId: string): Promise<any[]> {
    const response = await this.client.get<any[]>(`/staff/shifts/${restaurantId}`);
    return response.data || [];
  }

  async createStaffShift(data: {
    restaurantId: string;
    branchId?: string;
    name: string;
    startTime: string;
    endTime: string;
    maxCapacity: number;
  }): Promise<any> {
    const response = await this.client.post('/staff/shifts', data);
    return response.data;
  }

  async updateStaffShift(shiftId: string, data: Partial<{
    name: string;
    startTime: string;
    endTime: string;
    maxCapacity: number;
    isActive: boolean;
  }>): Promise<void> {
    await this.client.put(`/staff/shifts/${shiftId}`, data);
  }

  async deleteStaffShift(shiftId: string): Promise<void> {
    await this.client.delete(`/staff/shifts/${shiftId}`);
  }

  async assignStaffToShift(shiftId: string, userId: string, date: string, notes?: string): Promise<any> {
    const response = await this.client.post(`/staff/shifts/${shiftId}/assign`, { userId, date, notes });
    return response.data;
  }

  async removeStaffAssignment(staffShiftId: string): Promise<void> {
    await this.client.delete(`/staff/roster/${staffShiftId}`);
  }

  async getStaffPerformance(restaurantId: string, from?: string, to?: string): Promise<any[]> {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const q = params.toString() ? `?${params.toString()}` : '';
    const response = await this.client.get<any[]>(`/staff/performance/${restaurantId}${q}`);
    return response.data || [];
  }

  async getStaffCommissions(restaurantId: string): Promise<any[]> {
    const response = await this.client.get<any[]>(`/staff/commissions/${restaurantId}`);
    return response.data || [];
  }

  async calculateCommissions(restaurantId: string, data: {
    periodStart: string;
    periodEnd: string;
    defaultCommissionRate: number;
  }): Promise<any> {
    const response = await this.client.post(`/staff/commissions/calculate/${restaurantId}`, data);
    return response.data;
  }

  async markCommissionPaid(commissionId: string): Promise<void> {
    await this.client.patch(`/staff/commissions/${commissionId}/pay`, {});
  }

  // ── Feedback ───────────────────────────────────────────────────────────────

  async getFeedback(restaurantId: string, page = 1, pageSize = 50): Promise<any[]> {
    const response = await this.client.get<any>(`/feedback/restaurant/${restaurantId}?page=${page}&pageSize=${pageSize}`);
    const raw = response.data;
    return Array.isArray(raw) ? raw : (raw?.data || []);
  }

  // ── Coupons ────────────────────────────────────────────────────────────────

  async getCoupons(restaurantId: string): Promise<any[]> {
    const response = await this.client.get<any[]>(`/coupon/restaurant/${restaurantId}`);
    return response.data || [];
  }

  async getCoupon(id: string): Promise<any> {
    const response = await this.client.get<any>(`/coupon/${id}`);
    return response.data;
  }

  async createCoupon(data: any): Promise<any> {
    const response = await this.client.post<any>('/coupon', data);
    return response.data;
  }

  async updateCoupon(id: string, data: any): Promise<any> {
    const response = await this.client.put<any>(`/coupon/${id}`, data);
    return response.data;
  }

  async toggleCouponStatus(id: string, isActive: boolean): Promise<void> {
    await this.client.patch(`/coupon/${id}/status`, { isActive });
  }

  async deleteCoupon(id: string): Promise<void> {
    await this.client.delete(`/coupon/${id}`);
  }

  async validateCoupon(data: { couponCode: string; restaurantId: string; orderAmount: number; userId?: string }): Promise<any> {
    const response = await this.client.post<any>('/coupon/validate', data);
    return response.data;
  }

  async applyCoupon(data: { couponCode: string; restaurantId: string; orderId: string; userId: string; orderAmount: number; discountAmount: number }): Promise<any> {
    const response = await this.client.post<any>('/coupon/apply', data);
    return response.data;
  }

  async getCouponStats(restaurantId: string): Promise<any> {
    const response = await this.client.get<any>(`/coupon/restaurant/${restaurantId}/stats`);
    return response.data;
  }

  // ── Discount Rules ─────────────────────────────────────────────────────────

  async getDiscountRules(restaurantId: string): Promise<any[]> {
    const response = await this.client.get<any[]>(`/coupon/discount-rules/restaurant/${restaurantId}`);
    return response.data || [];
  }

  async createDiscountRule(data: any): Promise<any> {
    const response = await this.client.post<any>('/coupon/discount-rules', data);
    return response.data;
  }

  async updateDiscountRule(id: string, data: any): Promise<any> {
    const response = await this.client.put<any>(`/coupon/discount-rules/${id}`, data);
    return response.data;
  }

  async deleteDiscountRule(id: string): Promise<void> {
    await this.client.delete(`/coupon/discount-rules/${id}`);
  }

  async evaluateDiscountRules(data: { restaurantId: string; orderAmount: number; menuItemId?: string; menuCategoryId?: string }): Promise<any[]> {
    const response = await this.client.post<any[]>('/coupon/discount-rules/evaluate', data);
    return response.data || [];
  }

  // ── Payment Gateways ───────────────────────────────────────────────────────

  async getAvailableGatewayTypes(): Promise<string[]> {
    const r = await this.client.get<string[]>('/payment/gateways');
    return r.data || [];
  }

  async getGatewayFields(name: string): Promise<any> {
    const r = await this.client.get<any>(`/payment/gateways/${name}/config`);
    return r.data;
  }

  async getRestaurantGateways(restaurantId: string): Promise<any[]> {
    const r = await this.client.get<any[]>(`/payment/restaurant/${restaurantId}/gateways`);
    return r.data || [];
  }

  async saveGateway(restaurantId: string, payload: {
    gatewayName: string; displayName?: string; isActive?: boolean; isTestMode?: boolean;
    apiKey?: string; apiSecret?: string; merchantId?: string; webhookUrl?: string;
    supportsCard?: boolean; supportsUPI?: boolean; supportsWallet?: boolean; supportsNetBanking?: boolean;
  }): Promise<any> {
    const r = await this.client.post<any>(`/payment/restaurant/${restaurantId}/gateways`, payload);
    return r.data;
  }

  async toggleGateway(gatewayId: string): Promise<any> {
    const r = await this.client.patch<any>(`/payment/gateways/${gatewayId}/toggle`, {});
    return r.data;
  }

  async deleteGateway(gatewayId: string): Promise<void> {
    await this.client.delete(`/payment/gateways/${gatewayId}`);
  }

  // ── Generic HTTP passthrough (for new feature components) ──────────────────

  async get<T = any>(url: string, params?: Record<string, any>): Promise<{ data: T }> {
    const response = await this.client.get<T>(url, params ? { params } : undefined);
    return { data: response.data };
  }

  async post<T = any>(url: string, data?: any): Promise<{ data: T }> {
    const response = await this.client.post<T>(url, data);
    return { data: response.data };
  }

  async put<T = any>(url: string, data?: any): Promise<{ data: T }> {
    const response = await this.client.put<T>(url, data);
    return { data: response.data };
  }

  async patch<T = any>(url: string, data?: any): Promise<{ data: T }> {
    const response = await this.client.patch<T>(url, data);
    return { data: response.data };
  }

  async delete<T = any>(url: string): Promise<{ data: T }> {
    const response = await this.client.delete<T>(url);
    return { data: response.data };
  }

  async sendWhatsAppOtp(phoneNumber: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post('/Otp/send', { phoneNumber });
    return response.data;
  }

  async verifyWhatsAppOtp(phoneNumber: string, otp: string): Promise<{ success: boolean; verified: boolean; message: string }> {
    const response = await this.client.post('/Otp/verify', { phoneNumber, otp });
    return response.data;
  }

  // WhatsApp Integration APIs
  async getWhatsAppStatus(): Promise<{ status: string; phone?: string; pushName?: string }> {
    const response = await this.client.get('/WhatsApp/status');
    return response.data;
  }

  async startWhatsAppSession(): Promise<{ status: string }> {
    const response = await this.client.post('/WhatsApp/start');
    return response.data;
  }

  async getWhatsAppQR(): Promise<{ qr: string }> {
    const response = await this.client.get('/WhatsApp/qr');
    return response.data;
  }

  async stopWhatsAppSession(): Promise<{ status: string }> {
    const response = await this.client.post('/WhatsApp/stop');
    return response.data;
  }

  async getOrderNumberSettings(restaurantId: string): Promise<{ dailyReset: boolean }> {
    const response = await this.client.get(`/restaurant/${restaurantId}/order-number-settings`);
    return response.data;
  }

  async saveOrderNumberSettings(restaurantId: string, dailyReset: boolean): Promise<any> {
    const response = await this.client.post(`/restaurant/${restaurantId}/order-number-settings`, { dailyReset });
    return response.data;
  }

  async getWhatsAppSettings(): Promise<{ autoSendInvoice: boolean; phone?: string }> {
    const response = await this.client.get('/WhatsApp/settings');
    return response.data;
  }

  async saveWhatsAppSettings(settings: { autoSendInvoice: boolean; phone?: string }): Promise<any> {
    const response = await this.client.post('/WhatsApp/settings', settings);
    return response.data;
  }

  async sendWhatsAppBill(orderId: string, customerPhone: string): Promise<any> {
    const response = await this.client.post(`/WhatsApp/send-bill`, { orderId, customerPhone });
    return response.data;
  }

  async sendWhatsAppReport(title: string, content: string): Promise<any> {
    const response = await this.client.post(`/WhatsApp/send-report`, { title, content });
    return response.data;
  }

  // Evolution API Multi-tenant WhatsApp Integration APIs
  async createWhatsAppInstance(instanceName?: string): Promise<any> {
    const response = await this.client.post('/WhatsApp/create-instance', { instanceName });
    return response.data;
  }

  async getWhatsAppQRNew(tenantId: string): Promise<{ qr: string }> {
    const response = await this.client.get(`/WhatsApp/qrcode/${tenantId}`);
    return response.data;
  }

  async getWhatsAppStatusNew(tenantId: string): Promise<{ status: string }> {
    const response = await this.client.get(`/WhatsApp/status/${tenantId}`);
    return response.data;
  }

  async disconnectWhatsAppNew(tenantId: string): Promise<any> {
    const response = await this.client.post(`/WhatsApp/disconnect/${tenantId}`);
    return response.data;
  }

  async sendWhatsAppInvoiceNew(orderId: string, customerPhone: string): Promise<any> {
    const response = await this.client.post('/WhatsApp/send-invoice', { orderId, customerPhone });
    return response.data;
  }

  async sendWhatsAppTestMessage(mobileNumber: string, message: string): Promise<any> {
    const response = await this.client.post('/WhatsApp/test-message', { mobileNumber, message });
    return response.data;
  }

  async getWhatsAppLogs(tenantId: string): Promise<any[]> {
    const response = await this.client.get(`/WhatsApp/logs/${tenantId}`);
    return response.data?.logs || [];
  }

  async getGSTConfig(): Promise<any> {
    const response = await this.client.get('/GST/config');
    return response.data;
  }

  async updateGSTConfig(config: any): Promise<any> {
    const response = await this.client.put('/GST/config', config);
    return response.data;
  }

  // ── Combo Management ─────────────────────────────────────────────────────────

  async getCombos(restaurantId: string, activeOnly = false): Promise<any[]> {
    const r = await this.client.get<any[]>(`/combo/restaurant/${restaurantId}?activeOnly=${activeOnly}`);
    return r.data || [];
  }

  async getComboById(id: string): Promise<any> {
    const r = await this.client.get<any>(`/combo/${id}`);
    return r.data;
  }

  async createCombo(data: {
    restaurantId: string;
    name: string;
    description?: string;
    comboPrice: number;
    isAvailable: boolean;
    displayOrder?: number;
    expiryDate?: string;
    availableFrom?: number;
    availableTo?: number;
    items: Array<{ menuItemId: string; quantity: number; isCustomisable?: boolean }>;
  }): Promise<any> {
    const r = await this.client.post<any>('/combo', data);
    return r.data;
  }

  async updateCombo(id: string, data: any): Promise<any> {
    const r = await this.client.put<any>(`/combo/${id}`, data);
    return r.data;
  }

  async toggleComboStatus(id: string): Promise<any> {
    const r = await this.client.patch<any>(`/combo/${id}/toggle`, {});
    return r.data;
  }

  async deleteCombo(id: string): Promise<void> {
    await this.client.delete(`/combo/${id}`);
  }

  // ── Customer CRM ──────────────────────────────────────────────────────────────

  async getCustomers(restaurantId: string, params?: { search?: string; tag?: string; page?: number; pageSize?: number }): Promise<{ data: any[]; total: number }> {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.tag) query.set('tag', params.tag);
    if (params?.page) query.set('page', String(params.page));
    if (params?.pageSize) query.set('pageSize', String(params.pageSize));
    const qs = query.toString();
    const r = await this.client.get<any>(`/customer/restaurant/${restaurantId}${qs ? '?' + qs : ''}`);
    const raw = r.data;
    return { data: Array.isArray(raw) ? raw : (raw?.data || []), total: raw?.total ?? 0 };
  }

  async createCustomer(data: { name: string; phone?: string; email?: string; address?: string; notes?: string; tag?: string; dateOfBirth?: string; anniversary?: string }): Promise<any> {
    const r = await this.client.post<any>('/customer', data);
    return r.data;
  }

  async updateCustomer(id: string, data: Partial<{ name: string; phone: string; email: string; address: string; notes: string; tag: string; dateOfBirth: string; anniversary: string }>): Promise<any> {
    const r = await this.client.put<any>(`/customer/${id}`, data);
    return r.data;
  }

  async deleteCustomer(id: string): Promise<void> {
    await this.client.delete(`/customer/${id}`);
  }
}

// Create and export the service instance
const apiService = new ApiService();
export default apiService;

// Export demo mode flag
export { isDemoMode };

// Demo mode login function (for testing without backend)
export const demoLogin = async (
  username: string,
  password: string,
  role: 'superadmin' | 'admin' | 'staff'
): Promise<{token: string, user: any, expiresAt: Date}> => {
  // Demo credentials
  const demoCredentials = {
    superadmin: { restaurantCode: 'DEMO', username: 'superadmin', password: 'Demo@123' },
    admin: { restaurantCode: 'DEMO', username: 'admin', password: 'Demo@123' },
    staff: { restaurantCode: 'DEMO', username: 'staff', password: 'Demo@123' },
  };
  
  const creds = demoCredentials[role];
  
  if (isDev) console.log('Demo login for role:', role, creds);
  
  // Generate a demo token
  const token = `demo-token-${role}-${Date.now()}`;
  
  // Return demo response
  return {
    token,
    user: {
      id: `${role}-user-id`,
      username: creds.username,
      email: `${creds.username}@demo.restaurant.com`,
      passwordHash: '',
      firstName: creds.username === 'superadmin' ? 'Super' : creds.username === 'admin' ? 'Admin' : 'Staff',
      lastName: 'User',
      role: role.toUpperCase(),
      status: 'Active',
      tenantId: '39eb1948-3eb5-4cdc-9b37-caea1a394f7d',
      restaurantId: 'a72df374-5613-47d7-af67-dd674c1859c9',
      createdAt: new Date(),
      updatedAt: new Date()
    },
    expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000) // 24 hours
  };
};
